import React from "react";
import {
    Container, CssBaseline, Typography, AppBar, Toolbar,
    Button, Radio, RadioGroup, FormControlLabel, FormControl, FormLabel,
    MenuItem, Select, InputLabel, Grid, Box, Paper, CircularProgress, IconButton,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { useRoastMySwingViewModel } from "./useRoastMySwingViewModel";

// Import required icons
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import PlayCircleOutlineIcon from '@material-ui/icons/PlayCircleOutline';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import VideoLibraryIcon from '@material-ui/icons/VideoLibrary';
import ClearIcon from '@material-ui/icons/Clear';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import FacebookIcon from '@material-ui/icons/Facebook';
import TwitterIcon from '@material-ui/icons/Twitter';


const useStyles = makeStyles((theme) => ({
    root: {
        backgroundColor: '#f4f6f8',
        minHeight: '100vh',
    },
    heroSection: {
        backgroundColor: '#24055D',
        color: '#FFFFFF',
        padding: theme.spacing(2, 0, 12, 0),
    },
    appBar: {
        backgroundColor: 'transparent',
        boxShadow: 'none',
    },
    navButtons: {
        '& > *': {
            marginLeft: theme.spacing(2),
        },
    },
    headerContent: {
        textAlign: 'start',
    },
    poweredBy: {
        fontWeight: 300,
        color: "#CBB1FB"
    },
    mainTitle: {
        fontFamily: "'Ag', sans-serif",
        fontWeight: 600,
        color: '#FFFFFF',
        textShadow: 'none',
    },
    mainCard: {
        position: 'relative',
        zIndex: 1,
        marginTop: theme.spacing(-8),
        backgroundColor: '#FFFFFF',
        color: theme.palette.text.primary,
        borderRadius: theme.spacing(2),
        padding: theme.spacing(3),
        margin: theme.spacing(0, 'auto', 4, 'auto'),
        maxWidth: '1024px',
        boxShadow: '0px 10px 25px rgba(0, 0, 0, 0.1)',
    },
    cardHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: theme.spacing(3),
        borderBottom: `1px solid ${theme.palette.divider}`,
        paddingBottom: theme.spacing(2),
    },
    dropzone: {
        border: "2px dashed #3f51b5",
        borderRadius: "8px",
        padding: theme.spacing(4),
        textAlign: "center",
        cursor: "pointer",
        color: "#3f51b5",
        marginBottom: theme.spacing(2),
        transition: "background-color 0.3s ease",
        "&:hover": {
            backgroundColor: "#f0f4ff",
        },
    },

    // Other styles remain unchanged
    emptyStateContainer: { textAlign: 'center', padding: theme.spacing(4, 2), cursor: 'pointer' },
    requirementsList: { listStyle: 'none', padding: 0, textAlign: 'left', display: 'inline-block', marginTop: theme.spacing(2), color: theme.palette.text.secondary },
    requirementItem: { display: 'flex', alignItems: 'center', marginBottom: theme.spacing(1) },
    checkIcon: { color: '#4caf50', marginRight: theme.spacing(1) },
    actionButtons: { marginTop: theme.spacing(4), '& > *': { margin: theme.spacing(0, 1), } },
    formContainer: { padding: theme.spacing(1, 0) },
    videoInfo: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', backgroundColor: '#f0f4ff', padding: theme.spacing(1.5, 2), borderRadius: theme.shape.borderRadius, marginBottom: theme.spacing(3) },
    fieldGroup: { marginTop: theme.spacing(2) },
    submitButton: { marginTop: theme.spacing(4), padding: theme.spacing(1.5), fontWeight: 'bold' },
    loadingContainer: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: theme.spacing(8, 2), textAlign: 'center' },
    resultVideo: { width: '100%', borderRadius: theme.shape.borderRadius, backgroundColor: '#000', maxHeight: '500px' },
    metricCard: { padding: theme.spacing(2), textAlign: 'center', border: `1px solid ${theme.palette.divider}`, borderRadius: theme.shape.borderRadius, height: '100%' },
    drillCard: { padding: theme.spacing(2), display: 'flex', alignItems: 'center', justifyContent: 'space-between', border: `1px solid ${theme.palette.divider}`, borderRadius: theme.shape.borderRadius, marginBottom: theme.spacing(1) },
    footerSection: { marginTop: theme.spacing(4), paddingTop: theme.spacing(3), borderTop: `1px solid ${theme.palette.divider}`, textAlign: 'center' },
    storeButtons: { marginTop: theme.spacing(1), '& img': { height: '40px', margin: theme.spacing(0, 1), cursor: 'pointer', } }
}));

const heightOptions = Array.from({ length: 31 }, (_, i) => {
    const totalInches = 54 + i;
    const feet = Math.floor(totalInches / 12);
    const inches = totalInches % 12;
    const label = `${feet}'${inches}"`;
    return { value: label, label };
});


// --- SUB-COMPONENTS (Implementations are unchanged) ---
const EmptyState = ({ getRootProps, getInputProps }) => {
    const classes = useStyles();
    return (
        <div {...getRootProps()} className={classes.emptyStateContainer}>
            <input {...getInputProps()} />
            <Box className={classes.dropzone}>
                <Typography>Drag and drop a video here, or click to select one</Typography>
            </Box>

            <Typography variant="h5" component="h2" gutterBottom>Think Your Swing is Perfect? Let Us Roast It!</Typography>
            <Typography variant="body1" color="textSecondary">  We'll give you a brutally honest (and slightly hilarious) breakdown of your technique. Make sure your video meets these requirements:</Typography>
            <ul className={classes.requirementsList}>
                <li className={classes.requirementItem}><CheckCircleIcon className={classes.checkIcon} /> Videos must be slow-mo / 120 FPS</li>
                <li className={classes.requirementItem}><CheckCircleIcon className={classes.checkIcon} /> Videos must be face-on (FO) or down the line (DTL)</li>
                <li className={classes.requirementItem}><CheckCircleIcon className={classes.checkIcon} /> MP4, AVI, or MOV video file</li>
            </ul>
            <Box className={classes.actionButtons}>
                <Button variant="text" startIcon={<PlayCircleOutlineIcon />}>See an example</Button>
                <Button variant="contained" color="primary" startIcon={<CloudUploadIcon />}>Upload a video</Button>
            </Box>
        </div>
    );
};
const MetadataForm = (props) => {
    const classes = useStyles();
    const { videoFile, setVideoFile, handleSubmit, gender, setGender, height, setHeight, club, setClub, swingAngle, setSwingAngle, useGpuServer, setUseGpuServer } = props;
    return (
        <form className={classes.formContainer} onSubmit={handleSubmit}>
            <Box className={classes.videoInfo}>
                <Box display="flex" alignItems="center">
                    <VideoLibraryIcon style={{ marginRight: '8px' }} />
                    <Typography variant="body1"><b>{videoFile.name}</b></Typography>
                </Box>
                <Button size="small" startIcon={<ClearIcon />} onClick={() => setVideoFile(null)}>
                    Change
                </Button>
            </Box>
            <Grid container spacing={3} className={classes.fieldGroup}>
                <Grid item xs={6}> <FormControl component="fieldset" fullWidth> <FormLabel component="legend">Gender</FormLabel> <RadioGroup row value={gender} onChange={(e) => setGender(e.target.value)}> <FormControlLabel value="male" control={<Radio />} label="Male" /> <FormControlLabel value="female" control={<Radio />} label="Female" /> </RadioGroup> </FormControl> </Grid>
                <Grid item xs={6}> <FormControl component="fieldset" fullWidth> <FormLabel component="legend">Club</FormLabel> <RadioGroup row value={club} onChange={(e) => setClub(e.target.value)}> <FormControlLabel value="D" control={<Radio />} label="Driver" /> <FormControlLabel value="7I" control={<Radio />} label="Iron" /> </RadioGroup> </FormControl> </Grid>
            </Grid>
            <Grid container spacing={3} className={classes.fieldGroup}>
                <Grid item xs={6}> <FormControl component="fieldset" fullWidth> <FormLabel component="legend">Swing Angle</FormLabel> <RadioGroup row value={swingAngle} onChange={(e) => setSwingAngle(e.target.value)}> <FormControlLabel value="FaceOn" control={<Radio />} label="Face On" /> <FormControlLabel value="DownTheLine" control={<Radio />} label="Down The Line" /> </RadioGroup> </FormControl> </Grid>
                <Grid item xs={6}> <FormControl fullWidth> <InputLabel id="height-label">Height*</InputLabel> <Select labelId="height-label" value={height} onChange={(e) => setHeight(e.target.value)} required> {heightOptions.map(({ value, label }) => (<MenuItem key={value} value={value}>{label}</MenuItem>))} </Select> </FormControl> </Grid>
            </Grid>
            <FormControl component="fieldset" fullWidth className={classes.fieldGroup}>
                <FormLabel component="legend">Analyze With</FormLabel>
                <RadioGroup row value={useGpuServer ? "gpu" : "server"} onChange={(e) => setUseGpuServer(e.target.value === "gpu")}> <FormControlLabel value="server" control={<Radio />} label="Sportsbox Server" /> <FormControlLabel value="gpu" control={<Radio />} label="GPU Server" /> </RadioGroup>
            </FormControl>
            <Button type="submit" fullWidth variant="contained" color="primary" className={classes.submitButton} disabled={!videoFile || !gender || !height || !club}>
                Generate Roast
            </Button>
        </form>
    );
};

const LoadingState = ({ message }) => {
    const classes = useStyles();
    return (<Box className={classes.loadingContainer}> <CircularProgress size={60} /> <Typography variant="h6" style={{ marginTop: '16px' }}> {message || "Processing..."} </Typography> </Box>);
};

const ResultState = ({ result, onReset }) => {
    const classes = useStyles();
    return (
        <Box>
            <Grid container spacing={4}>
                <Grid item xs={12} md={5}>
                    <Typography variant="h4" component="h3" gutterBottom style={{ fontWeight: 'bold' }}> {result.roastTitle} </Typography>
                    <Typography variant="body1" color="textSecondary" paragraph> {result.roastText} </Typography>
                    <Typography variant="h6" component="h4" style={{ marginTop: '24px' }} gutterBottom> Top 4 Swing Metrics </Typography>
                    <Grid container spacing={2}> {result.metrics.map(metric => (<Grid item xs={6} key={metric.label}> <Paper className={classes.metricCard} variant="outlined"> <Typography variant="h5" style={{ fontWeight: 'bold' }}>{metric.value}</Typography> <Typography variant="caption" color="textSecondary">{metric.label}</Typography> </Paper> </Grid>))} </Grid>
                    <Typography variant="h6" component="h4" style={{ marginTop: '24px' }} gutterBottom> Improve your swing with these adjustments </Typography>
                    {result.drills.slice(0, 2).map(drill => (<Paper key={drill} className={classes.drillCard} variant="outlined"> <Typography variant="body2" style={{ fontWeight: 500 }}>{drill}</Typography> <ChevronRightIcon /> </Paper>))}
                </Grid>
                <Grid item xs={12} md={7}> <video className={classes.resultVideo} src={result.videoUrl} controls autoPlay loop muted playsInline /> </Grid>
            </Grid>
            <Box className={classes.footerSection}>
                <Typography variant="body2">Share your roast, challenge your friends, and discover how our complete app can transform your golf game from laughable to enviable.</Typography>
                <Box>
                    <IconButton><FacebookIcon /></IconButton>
                    <IconButton><TwitterIcon /></IconButton>
                </Box>
            </Box>
            <Box className={classes.footerSection}>
                <Typography variant="h6">Supercharge your golf game improvement.</Typography>
                <Box className={classes.storeButtons}>
                    <a href="https://play.google.com/store/apps/details?id=com.sportsbox.golfai" target="_blank" rel="noopener noreferrer">
                        <img src="/images/google.svg" alt="Get it on Google Play" height="57" />
                    </a>
                    <a href="https://apps.apple.com/us/app/sportsbox-3d-golf/id1578921026" target="_blank" rel="noopener noreferrer">
                        <img src="/images/apple.svg" alt="Download on the App Store" height="57" />
                    </a>
                </Box>
            </Box>
            <Box className={classes.footerSection}>
                <Button variant="contained" color="primary" onClick={onReset}> Upload Another Video </Button>
            </Box>
        </Box>
    );
};

const RoastMySwingPage = () => {
    const classes = useStyles();
    const viewModel = useRoastMySwingViewModel();
    const { videoFile, isLoading, progressMessage, analysisResult, handleReset, getRootProps, getInputProps } = viewModel;

    const renderContent = () => {
        if (isLoading) return <LoadingState message={progressMessage} />;
        if (analysisResult) return <ResultState result={analysisResult} onReset={handleReset} />;
        if (videoFile) return <MetadataForm {...viewModel} />;
        return <EmptyState getRootProps={getRootProps} getInputProps={getInputProps} />;
    }

    return (
        <Box className={classes.root}>
            <CssBaseline />

            <Box className={classes.heroSection}>
                <AppBar position="static" className={classes.appBar}>
                    <Container maxWidth="lg">
                        <Toolbar disableGutters>
                            <Box flexGrow={1} />
                            <Box className={classes.navButtons}>
                                <Button color="inherit">Visit Website</Button>
                                <Button variant="contained">Download App</Button>
                            </Box>
                        </Toolbar>
                    </Container>
                </AppBar>

                <Container maxWidth="md" className={classes.headerContent}>
                    <Typography variant="h6" component="p" className={classes.poweredBy}>Powered by Sportsbox AI</Typography>
                    <Typography variant="h3" className={classes.mainTitle}>Roast my swing 🤠</Typography>
                </Container>
            </Box>

            {/* The main content card is now outside the hero section and pulled up with a negative margin */}
            <Container>
                <Paper className={classes.mainCard} elevation={8}>
                    <Box className={classes.cardHeader}>
                        <Typography variant="h6" component="h2"> {analysisResult ? "Your Roast Result" : "Import a video"}</Typography>
                        {!videoFile && !analysisResult && (
                            <Button {...getRootProps()} variant="outlined" color="primary" startIcon={<CloudUploadIcon />}>
                                <input {...getInputProps()} />
                                Upload
                            </Button>
                        )}
                    </Box>
                    {renderContent()}
                </Paper>
            </Container>
        </Box>
    );
};

export default RoastMySwingPage;